import React, { useEffect, useRef, useContext, useState } from "react";
import Accordion from 'react-bootstrap/Accordion';
import AccordionContext from 'react-bootstrap/AccordionContext';
import SelectableContext from 'react-bootstrap/SelectableContext';
import { useAccordionToggle } from 'react-bootstrap/AccordionToggle';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import { Formik } from 'formik';
import moment from 'moment';
import { connect } from 'react-redux';
import * as yup from 'yup';
import { useHistory } from 'react-router-dom';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { startCase, snakeCase } from 'lodash';
import { getUTMParamsFromURL } from '../helpers/utm';

import { IMaskMixin } from 'react-imask';
const MaskedInput = IMaskMixin(({inputRef, ...props}) => ( <Form.Control {...props} ref={inputRef} />));

import store from '../helpers/store'
import stateSelectOptions from '../helpers/states'
import {
  availabilityOptionText,
  formattedChosenAvailability,
  filteredMorningAfternoonAvailabilities
} from '../helpers/date'

let Years = []
const lastYear = window.gon.max_vehicle_year;
const firstYear = window.gon.min_vehicle_year;

// Show or hide the Find Vehicle panel feature
const findVehicleEnabled = window.gon.find_vehicle_enabled;

// Show or hide the opt-in consent for text messaging
const requireOptInConsent = window.gon.require_opt_in_consent;

for (let year = Number(lastYear); year >= firstYear; year--) {
  Years.push(String(year));
}

const requiredString = 'This field is required.';
const phoneRegExp = /^\(\d{3}\) \d{3}-\d{4}$/;
const vinRegExp = /^[(A-H|J-N|P|R-Z|0-9)]{17}$/;
const mileageRegExp = /^[0-9]+$/;
const zipRegExp = /^\d\d\d\d\d(-\d\d\d\d)?$/

const emptySchemaField = yup.object().shape({});

const defaultValidationSchema = yup.object().shape({
  make: yup.string().required(),
  year: yup
    .number()
    .required()
    .integer(requiredString)
    .positive(requiredString),
  model: yup.string().required(requiredString),
  mileage: yup.string().matches(mileageRegExp, 'Mileage is not valid. Please enter only numbers.'),
  name: yup.string().required(requiredString),
  email: yup.string().email().required(requiredString),
  phoneNumber: yup.string().matches(phoneRegExp, 'Phone number is not valid').required(requiredString).test('non-repeating', 'Please provide a valid phone number.', (val) => {
    if(val === undefined) return false;
    let uniqueDigits = val.replace(/\D/g, '').split('').filter((v, i, a) => a.indexOf(v) === i);
    return uniqueDigits.length > 1;
  }),
  concern: yup
    .number()
    .required()
    .positive(requiredString)
    .integer(requiredString),
  advisor: yup
    .number()
    .required()
    .integer(requiredString),
  appointment: yup.string().required(requiredString),
  concernDescriptionAdditionalDetails: yup.string().test('len', 'Must be 250 characters or less', (val) => {
    if(val === undefined) return true;
    if(val.split('\n')[1] == undefined) return true;
    return val.split('\n')[1].length <= 250;
  }),
  additionalConcerns: yup.string(),
});

const minimumPrimaryConcernDescriptionLength = 10;
const requiredPrimaryConcernDescriptionValidationSchema = defaultValidationSchema.concat(
  yup.object().shape({
    concernDescriptionAdditionalDetails: yup.string().required(requiredString).test('len', 'Please provide a little more detail about your primary concern.', (val) => {
      if(val === undefined) return false;
      return val.length >= minimumPrimaryConcernDescriptionLength;
    })
  })
);

const findVehicleValidationSchema = defaultValidationSchema.concat(
  yup.object().shape({
    findPhoneNumber: yup.string().matches(phoneRegExp, 'Phone number is not valid').required(requiredString),
    findEmailAddress: yup.string().email()
  })
);

const requiredVinValidationSchema = defaultValidationSchema.concat(
  yup.object().shape({
    vin: yup.string().matches(vinRegExp, 'VIN is not valid').required(requiredString),
  })
);

const requiredMileageValidationSchema = defaultValidationSchema.concat(
  yup.object().shape({
    mileage: yup.string().matches(mileageRegExp, 'Mileage is not valid. Please enter only numbers.').required(requiredString),
  })
);

const requiredAddressValidationSchema = defaultValidationSchema.concat(
  yup.object().shape({
    address: yup.string().required(requiredString),
    city: yup.string().required(requiredString),
    state: yup.string().required(requiredString),
    postal_code: yup.string().matches(zipRegExp, 'Postal Code is not valid').required(requiredString),
    special_instructions: yup.string()
  })
);

const optInMessage = 'You must indicate that you agree to our text message program terms by checking the box.';
const requiredOptInValidationSchema = defaultValidationSchema.concat(
  yup.object().shape({
    optInConsent: yup.array().min(1, optInMessage).required(optInMessage).nullable()
  })
);

const DisabledWhileAccordionIsOpenButton = ({ children, disabled,...props }) => {
  const currentEventKey = useContext(AccordionContext);

  return (
    <Button {...props} disabled={disabled || currentEventKey}>
      { children }
    </Button>
  );
}

const ParentAccordionContext = React.createContext(null);
ParentAccordionContext.displayName = 'ParentAccordionContext';

const ChildAccordion = ({ children, parentEventKey, ...props }) => {
  const onSelect = useContext(SelectableContext);
  const contextEventKey = useContext(AccordionContext);

  const useParentAccordionToggle = (eventKey, onClick) => (e) => {
    let eventKeyPassed = eventKey === contextEventKey ? null : eventKey;

    onSelect(eventKeyPassed, e);
    if (onClick) onClick(e);
  }

  return (
    <ParentAccordionContext.Provider value={useParentAccordionToggle}>
      <Accordion {...props}>
        { children }
      </Accordion>
    </ParentAccordionContext.Provider>
  );
}

const ChildAccordionToggle = ({ children, eventKey, onClick, ...props }) => {
  const useParentAccordionToggle = useContext(ParentAccordionContext);
  const decoratedOnClick = useParentAccordionToggle(eventKey, onClick);

  return (
    <Accordion.Toggle {...props} eventKey="" onClick={decoratedOnClick}>
      { children }
    </Accordion.Toggle>
  );
}
ChildAccordion.Toggle = ChildAccordionToggle;

const AppointmentForm = props => {
  const [showSubmit, setShowSubmit] = useState(false);
  const [validationSchema, setValidationSchema] = useState(defaultValidationSchema);

  // Appointment search
  const [appointmentSearchInProgress, setAppointmentSearchInProgress] = useState(false);

  // Skill classification
  const [fetchingSkillClassifications, setFetchingSkillClassifications] = useState(false);
  const [matchedSkillClassification, setMatchedSkillClassification] = useState(false);
  const [failedMatchedSkillClassification, setFailedMatchedSkillClassification] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState(null);

  // Customer vehicle lookup
  const [vehicleSearchToken, setVehicleSearchToken] = useState(null);
  const [vehicleSearchComplete, setVehicleSearchComplete] = useState(!findVehicleEnabled);
  const [vehicleSearchInProgress, setVehicleSearchInProgress] = useState(false);
  const [vehicleSearchNoVehicleFound, setVehicleSearchNoVehicleFound] = useState(false);
  const [vehicleSearchResultCount, setVehicleSearchResultCount] = useState(null);
  const [vehicleSearchResultIncludedEmail, setVehicleSearchResultIncludedEmail] = useState(false);
  const [vehicleSearchErrorMessage, setVehicleSearchErrorMessage] = useState(null);
  const [showVerificationConfirmationControls, setShowVerificationConfirmationControls] = useState(false);
  const [confirmationCodeErrorMessage, setConfirmationCodeErrorMessage] = useState(null);
  const [confirmationCodeSuccessMessage, setConfirmationCodeSuccessMessage] = useState(null);

  useEffect( () => {
    props.fetchConcerns();
    props.fetchAdvisors();
    props.fetchOrganization();
  }, []);

  let history = useHistory();

  const _handleSubmit = async (values, actions) => {
    await props.updateAppointment(values);

    // Get the UTM parameters from the current URL
    const utmData = getUTMParamsFromURL();

    // Convert the UTM parameters to query string format
    const nextUrlParams = new URLSearchParams(utmData);

    // Construct the next URL with the UTM parameters included
    const nextUrl = `/${props.rootPath}/review?${nextUrlParams.toString()}`;

    // Navigate to the next page with the updated URL
    history.push(nextUrl);
  };

  const maxDate = moment().add(8, 'months').toDate();

  return (
    <Formik
      initialValues={props.appointment}
      onSubmit={_handleSubmit}
      validationSchema={validationSchema}
    >
      {
        formProps => {
          const _getValueFromEvent = event =>
            event.target.options[event.target.options.selectedIndex].value;

          const enableAIClassificationAssistant =
            props.concerns[formProps.values.concern] &&
            props.concerns[formProps.values.concern]
              .attributes.ai_classification_assistant == "yes";

          // Require primmary concern description when scheduling direct to technician
          // so we can attempt to match their description to the proper concern skill.
          // Only required when AI classification has not been disabled for the skill.
          const primaryConcernDescriptionRequired = window.gon.shoploader_direct_technician && enableAIClassificationAssistant;
          const primaryConcernDescriptionPlaceholder = !primaryConcernDescriptionRequired
            ? 'If you have additional details about your primary concern enter them here. (optional)'
            : 'Please provide additional details about your primary concern. (required)'

          // Require VIN if the chosen concern requires it.
          // We display VIN if the settings is not 'no' because this will handle
          // - ask_for_vin is just undefined; default behavior is to show
          // - ask_for_vin is 'required'
          // - ask_for_vin is 'optional'
          const vinDisplayed = props.concerns[formProps.values.concern]
                            && props.concerns[formProps.values.concern].attributes
                            && props.concerns[formProps.values.concern].attributes.ask_for_vin != 'no'

          // - ask_for_vin is 'required'
          const vinRequired  = props.concerns
                            && props.concerns[formProps.values.concern]
                            && props.concerns[formProps.values.concern].attributes
                            && props.concerns[formProps.values.concern].attributes.ask_for_vin == 'required';

          // Require a value for Find Phone Number if the Find Vehicle panel is enabled.
          const findPhoneRequired = findVehicleEnabled;

          // Require Mileage if the chosen concern requires it.
          // We display Mileage if the settings is not 'no' because this will handle
          // - ask_for_mileage is just undefined; default behavior is to show
          // - ask_for_mileage is 'required'
          // - ask_for_mileage is 'optional'
          const mileageDisplayed = props.concerns[formProps.values.concern]
                            && props.concerns[formProps.values.concern].attributes
                            && props.concerns[formProps.values.concern].attributes.ask_for_mileage != 'no'

          // - ask_for_mileage is 'required'
          const mileageRequired  = props.concerns
                            && props.concerns[formProps.values.concern]
                            && props.concerns[formProps.values.concern].attributes
                            && props.concerns[formProps.values.concern].attributes.ask_for_mileage == 'required';

          // All vehicle information entered when:
          // - model has been provided, since its last in the flow, and only accessible with a year and make
          // - VIN has been provided, if the VIN is required
          const vehicleInformationEntered = formProps.values.model
            && !(vinRequired && formProps.errors.vin)
            && !(mileageRequired && formProps.errors.mileage);

          // Try to decode VIN after it has been updated/validated
          useEffect(() => {
            let vin = formProps.values.vin;
            if (vin && vin.length >= 17 && !formProps.errors.vin) {
              props.fetchDecodeVin({ vin: formProps.values.vin });
            }
          }, [formProps.values?.vin, formProps.errors?.vin]);

          useEffect(() => {
            if (props.appointmentSearchCompletedAt) {
              setAppointmentSearchInProgress(false);
            }
          }, [props.appointmentSearchCompletedAt]);

          if (window.gon.shoploader_direct_technician) {
            // Trigger a skill classification when concern description field is filled.
            useEffect(() => {
              if (!enableAIClassificationAssistant) return;

              let customerDescription = formProps.values?.concernDescriptionAdditionalDetails;

              if (customerDescription && customerDescription.length >= minimumPrimaryConcernDescriptionLength) {
                // Clear any existing typing timeout
                if (typingTimeout) {
                  setFetchingSkillClassifications(false);
                  clearTimeout(typingTimeout);
                }

                // Set a new timeout to fire the API call after
                // typing stops. Adjust the timeout as needed
                const fetchClassificationDelay = 1000;

                const newTypingTimeout = setTimeout(() => {
                  setFetchingSkillClassifications(true);
                  setFailedMatchedSkillClassification(false);
                  formProps.setFieldValue('potentiallyMatchingSkills', []);
                  let classifications = props.fetchSkillClassifications({ description: customerDescription });
                }, fetchClassificationDelay);

                // Store the new timeout in the state
                setTypingTimeout(newTypingTimeout);
              }
            }, [formProps.values?.concernDescriptionAdditionalDetails, formProps.errors?.concernDescriptionAdditionalDetails]);

            // Clean up the timeout when the component unmounts or when relevant dependencies change
            useEffect(() => {
              if (!enableAIClassificationAssistant) return;

              return () => {
                if (typingTimeout) {
                  setFetchingSkillClassifications(false);
                  clearTimeout(typingTimeout);
                }
              };
            }, [typingTimeout]);

            // When skillClassifications are fetched
            useEffect(() => {
              if (!enableAIClassificationAssistant) return;

              setFetchingSkillClassifications(false);

              if (props.skillClassifications) {
                if (props.skillClassifications.success) {
                  // Sort relevant skills by relevance.
                  let mostRelevantSkills = props.skillClassifications.data.sort((a, b) => b.relevance - a.relevance);

                  if (mostRelevantSkills[0]?.id == formProps.values.concern) {
                    // If the user already chose the most relevant skill, we don't need to prompt them to choose a match.
                    setMatchedSkillClassification(true);
                  } else if (mostRelevantSkills[0]?.id != formProps.values.concern && mostRelevantSkills.length > 0) {
                    // If they chose a different skill than was classified, show them some options.
                    setMatchedSkillClassification(false);
                    formProps.setFieldValue('potentiallyMatchingSkills', mostRelevantSkills);
                  } else if (props.skillClassifications.data.length == 0) {
                    // We didn't get any matching skills from classification.
                    setFailedMatchedSkillClassification(true);
                    setMatchedSkillClassification(false);
                    formProps.setFieldValue('potentiallyMatchingSkills', []);
                  }
                }
              }
            }, [
              props.skillClassifications.success,
              props.skillClassifications.error,
              props.skillClassifications.data,
              props.skillClassifications.data?.length
            ]);
          }

          // When a Mobile Phone number and/or Email is submitted for customer lookup.
          // This is the initial vehicle search when no token is present.
          useEffect(() => {
            // If a token is already set, the vehicle search will be handled
            // by Repeat vehicle lookup useEffect found further below.
            if (formProps.values.verificationToken) {
              return;
            }

            if (formProps.values.findEmailAddress) {
              setVehicleSearchResultIncludedEmail(true);
            } else {
              setVehicleSearchResultIncludedEmail(false);
            }

            let findPhoneNumber = formProps.values.findPhoneNumber;
            if (vehicleSearchInProgress && !formProps.errors.findPhoneNumber) {
              props.fetchFindVehicle({
                phoneNumber: formProps.values.findPhoneNumber,
                emailAddress: formProps.values.findEmailAddress
              });
            }
          }, [vehicleSearchInProgress]);

          // Handle vehicle search results
          useEffect(() => {
            if(Object.keys(props.findVehicle).length != 0) {
              setVehicleSearchInProgress(false);
            }

            if (props.findVehicle && props.findVehicle.vehicles && props.findVehicle.vehicles.length == 0) {
              // Search resulted in no vehicles found.
              setVehicleSearchNoVehicleFound(true);
              setVehicleSearchResultCount(0);
            } else if (props.findVehicle && props.findVehicle.count > 0) {
              // Search resulted in multiple customers found with the same phone number.
              setVehicleSearchResultCount(props.findVehicle.count);
            } else if (props.findVehicle && props.findVehicle.count == 0) {
              // Search resulted for the phone number was successful,
              // but there was no result for the provided email address.
              setVehicleSearchNoVehicleFound(true);
              setVehicleSearchErrorMessage(null);
              setVehicleSearchResultCount(0);
            } else if (props.findVehicle.error) {
              // Search resulted in an error with a provided error message.
              setVehicleSearchNoVehicleFound(true);
              setVehicleSearchErrorMessage(props.findVehicle.error)
              setVehicleSearchResultCount(0);
            } else if (props.findVehicle.success === false) {
              // request was not successful, and no error message given
              // set error message to null to use standard error message.
              setVehicleSearchNoVehicleFound(true);
              setVehicleSearchErrorMessage(null);
              setVehicleSearchResultCount(0);
            }
          }, [props.findVehicle.searchCompletedAt]);

          // Send verification code when we've narrowed results down to a single customer.
          useEffect(() => {
            if (vehicleSearchResultCount == 1) {
              props.fetchCreateVerification({
                phoneNumber: formProps.values.findPhoneNumber,
                emailAddress: formProps.values.findEmailAddress
              });
              setShowVerificationConfirmationControls(true);
            }
          }, [vehicleSearchResultCount]);

          // Handle results of confirmation attempt. Set success or error message.
          useEffect(() => {
            if (props.updateVerification.data && props.updateVerification.data.error) {
              setConfirmationCodeErrorMessage(props.updateVerification.data.error);
              setConfirmationCodeSuccessMessage(null);
            }

            else if (props.updateVerification.data && props.updateVerification.data.success) {
              setConfirmationCodeErrorMessage(null);
              setConfirmationCodeSuccessMessage(props.updateVerification.data.success);

              if (props.updateVerification.data.token) {
                formProps.setFieldValue('verificationToken', props.updateVerification.data.token);
              }
            }
          }, [props.updateVerification.data])

          // Repeat vehicle lookup with token when confirmation was successful.
          useEffect(() => {
            if (formProps.values && formProps.values.verificationToken) {
              setVehicleSearchInProgress(true);
              props.fetchFindVehicle({
                phoneNumber: formProps.values.findPhoneNumber,
                emailAddress: formProps.values.findEmailAddress,
                token: formProps.values.verificationToken
              });
            }
          }, [formProps.values?.verificationToken]);

          // When a VIN is decoded successfully, try to
          // populate the Year Make and Model using the
          // decoded data provided by the decode API.
          useEffect(() => {
            if(props.decodeVin && props.decodeVin.success) {
              _handleDecodedVin(props.decodeVin.data)
            }
          }, [
            props.decodeVin.success,
            props.decodeVin.data?.year,
            props.decodeVin.data?.make,
            props.decodeVin.data?.model
          ]);

          const _handleFindVehicle = () => {
            setVehicleSearchInProgress(true);
            setVehicleSearchNoVehicleFound(false);
            setVehicleSearchResultCount(null);
          };

          const _resetConfirmationVerification = () => {
            formProps.setFieldValue('confirmationCode', '');
            formProps.setFieldValue('verificationToken', '');
            setConfirmationCodeErrorMessage(null);
            setConfirmationCodeSuccessMessage(null);
          }

          const _handleConfirmVerification = code => {
            if (code != "") {
              props.fetchUpdateVerification({
                phoneNumber: formProps.values.findPhoneNumber,
                code: formProps.values.confirmationCode
              });
            } else {
              setConfirmationCodeErrorMessage("Please enter your confirmation code.")
            }
          }

          const _handleChangeYear = year => {
            formProps.setFieldValue('year', year);
            if (year)
              props.fetchMakes({ year });
            formProps.setFieldValue('make', '');
            formProps.setFieldValue('model', '');
            props.clearModels();
          };

          const _handleChangeMake = make => {
            formProps.setFieldValue('make', make);
            if (make && formProps.values.year) {
              props.fetchModels({ year: formProps.values.year, makeId: make });
            }
            formProps.setFieldValue('model', '');
          };

          const _handleChangeModel = model => {
            formProps.setFieldValue('model', model);
          }

          const _handleChangeVin = vin => {
            formProps.setFieldValue('vin', vin);
          }

          const _handleChangeMileage = mileage => {
            formProps.setFieldValue('mileage', mileage);
          }

          const _handleOptInConsentChange = optInConsent => {
            if(optInConsent == 'on') {
              formProps.setFieldValue('optInConsent', [optInConsent]);
            } else {
              _clearOptInConsent();
            }
          }

          const _clearOptInConsent = () => {
            formProps.setFieldValue('optInConsent', null);
          }

          // _handleDecodedVin is a combo of _handleChangeYear, _handleChangeMake,
          // and _handleChangeModel but each is executed and awaited so that the
          // usual requests for vehicle make models will be handled sequentially.
          const withAsync = (func) => async (...args) => func(...args);
          const asyncHandleChangeYear = withAsync(_handleChangeYear);
          const asyncHandleChangeMake = withAsync(_handleChangeMake);

          async function _handleDecodedVin(data) {
            let { year, make, makeId, model } = data;
            await asyncHandleChangeYear(year);
            await asyncHandleChangeMake(make);
            _handleChangeModel(model);
          };

          async function _handleChooseFoundVehicle(data) {
            // set vehicle information
            let { vehicle_year, vehicle_make, vehicle_model, vehicle_vin } = data;
            await asyncHandleChangeYear(vehicle_year);
            await asyncHandleChangeMake(vehicle_make);
            _handleChangeModel(vehicle_model);
            _handleChangeVin(vehicle_vin || '');

            // Preset the customer information since it was successfully verified.
            let { customer_name, customer_phone, customer_email } = data;
            formProps.setFieldValue('name', customer_name);
            formProps.setFieldValue('phoneNumber', customer_phone);
            formProps.setFieldValue('email', customer_email);

            // set DMS customer information
            let { customer_number, vehicle_id } = data;
            formProps.setFieldValue('customerNumber', customer_number);
            formProps.setFieldValue('vehicleId', vehicle_id);

            // Force opt-in consent to be reconfirmed
            _clearOptInConsent();
          };

          const _handleVehicleNotListed = () => {
            // Empty year value will show Your Vehicle panel
            // while not preselecting any year button within
            _handleChangeYear(' ');
          }

          const _handleChangeConcern = (concern, concernDetail = '') => {
            formProps.setFieldValue('concern', concern);
            formProps.setFieldValue('concernDetail', concernDetail);

            if (concern) {
              fetchAppointments({
                advisorId: formProps.values.advisor,
                concernId: concern,
                transportationRequest: formProps.values.transportationRequest
              });
              props.fetchAdvisors({
                concernId: concern
              });
            }
          };

          const _handleChangeTransportationRequest = (transportationRequest) => {
            const currentValue = formProps.values.transportationRequest;

            // If the clicked value is the same as the current value, uncheck it by setting to null
            const newValue = currentValue === transportationRequest ? null : transportationRequest;

            formProps.setFieldValue('appointment', null); // force reselection of appointment
            formProps.setFieldValue('advisor', null); // force reselection of advisor
            formProps.setFieldValue('transportationRequest', newValue);

            props.fetchAdvisors({
              concernId: formProps.values.concern,
              transportationRequest: newValue
            });
          };

          const _handleChangeAdvisor = advisor => {
            formProps.setFieldValue('advisor', advisor);
            formProps.setFieldValue('appointmentDate', null);
            if (formProps.values.concern)
              fetchAppointments({
                advisorId: advisor,
                concernId: formProps.values.concern,
                transportationRequest: formProps.values.transportationRequest
              });
          };

          const _handleSelectAppointment = item => {
            // Force opt-in consent to be reconfirmed
            _clearOptInConsent();

            formProps.setFieldValue('appointment', item.id);
          }

          const concernAttributes = props.concerns[formProps.values.concern] && props.concerns[formProps.values.concern].attributes || {};
          const isMobileConcern = concernAttributes.is_mobile;

          // When the concern calls for showing Morning/Afternoon only
          // then only show the first morning and first afternoon option
          let appointments = props.appointments(formProps.values.advisor);
          if (concernAttributes && concernAttributes.am_pm_availability_selection && appointments && appointments[0] && appointments[0].length) {
            appointments = [filteredMorningAfternoonAvailabilities(appointments[0])]
          }

          const appointmentMoment = (
            formProps.values.appointmentDate ?
              moment(formProps.values.appointmentDate) :
              (
                formProps.values.advisor &&
                Array.isArray(appointments) && appointments.length > 0 &&
                Array.isArray(appointments[0]) && appointments[0].length > 0 &&
                appointments[0][0] ?
                  appointments[0][0].moment :
                  moment()
              )
          ).clone()

          const appointmentDate = appointmentMoment.toDate();

          const _hasPriorDate = parseInt(appointmentMoment.format('YYYYDDDD')) > parseInt(moment(props.minDate).format('YYYYDDDD'));
          const _hasNextDate = parseInt(appointmentMoment.format('YYYYDDDD')) < parseInt(moment(maxDate).format('YYYYDDDD'));

          const _handlePreviousDate = () => {
            if (_hasPriorDate)
              _handleDateChange(moment(appointmentDate).subtract(1,'day').toDate());
          }

          const _handleNextDate = () => {
            if (_hasNextDate)
              _handleDateChange(moment(appointmentDate).add(1,'day').toDate());
          }

          const fetchAppointments = (fetchArgs) => {
            // Direct to tech can be a bit slower on average when finding
            // an availability, whereas non direct to tech usually remains
            // fast enough to not require a search in progress indicator.
            // Show search in progress message for direct to tech only.
            if (window.gon.shoploader_direct_technician) {
              setAppointmentSearchInProgress(true);
            }
            props.fetchAppointments(fetchArgs);
          }

          const _handleDateChange = (date) => {
            formProps.setFieldValue('appointmentDate', moment(date).format());
            fetchAppointments({
              advisorId: formProps.values.advisor,
              concernId: formProps.values.concern,
              preferredDate: moment(date).format(),
              transportationRequest: formProps.values.transportationRequest
            });
          };

          const appointmentTime =
            formProps.values.appointment &&
            props.unfilteredAppointments[formProps.values.appointment] &&
            props.unfilteredAppointments[formProps.values.appointment].attributes &&
            props.unfilteredAppointments[formProps.values.appointment].attributes.appointment_date_time ?
              moment(
                props.unfilteredAppointments[formProps.values.appointment].attributes.appointment_date_time
              ) :
            '';

          const appointmentAdvisor =
            formProps.values.appointment &&
            props.unfilteredAppointments[formProps.values.appointment] &&
            props.unfilteredAppointments[formProps.values.appointment].attributes ?
              props.unfilteredAppointments[formProps.values.appointment].attributes.advisor_name :
              '';

          const _makeName =
            formProps.values.make && props.makes[formProps.values.make] && props.makes[formProps.values.make].attributes ?
              props.makes[formProps.values.make].attributes.make_display :
              formProps.values.make;

          const _modelName =
            formProps.values.model && props.models[formProps.values.model] && props.models[formProps.values.model].attributes ?
              props.models[formProps.values.model].attributes.model_name :
              formProps.values.model;

          const showFindVehicle = appointmentTime && findVehicleEnabled && !vehicleSearchComplete;
          const showFindVehicleEmailAddress = formProps.touched.findPhoneNumber
                                                && !formProps.errors.findPhoneNumber
                                                && (vehicleSearchResultCount > 1 || vehicleSearchResultIncludedEmail || formProps.values.findEmailAddress)

          const vehicleYearMakeModelEntered = formProps.values.year && _makeName && _modelName;

          const defaultVehicleSearchErrorMessage = (message) => (
            <>
              {message}<br/>
              or click
              <Accordion.Toggle
                as={Button}
                id="chooseYourVehicleLink"
                variant="link"
                eventKey="Vehicle"
                disabled={formProps.errors.additionalConcerns || formProps.errors.concernDescriptionAdditionalDetails}
              >
                Your Vehicle
              </Accordion.Toggle>
              below to continue.
            </>
          )

          const _removeChosenVehicleInfo = () => {
            // It might be nice if we avoid resetting if the user ends
            // up selecting the same vehicle information as the one found
            // via mobile phone search, when clicking into Your Vehicle.
            //
            // If they do end up clicking any buttons in that section after
            // having chosen a found vehicle, we assume that they choose a
            // different vehicle altogether so we reset the vehicle VIN and DMS
            // identifiers so as to not attach to the wrong customer/vehicle.
            formProps.setFieldValue('customerNumber', '');
            formProps.setFieldValue('vehicleId', '');
            formProps.setFieldValue('vin', '');
          }

          const _handleReview = () => { setShowSubmit(true); }

          // Ask for address if:
          //  - the concern chosen makes it optional or required
          //  - the transportation type chosen makes it optional or required
          const askForAddressConcernSetting = concernAttributes.ask_for_address;
          const askForAddressTransportationSetting = (formProps.values.transportationRequest && props.organization[snakeCase(formProps.values.transportationRequest || '')+'_ask_for_address_online']) || 'no';

          let askForAddress = (askForAddressConcernSetting && askForAddressConcernSetting != "no") ||
                              (askForAddressTransportationSetting && askForAddressTransportationSetting != "no");

          if (askForAddressConcernSetting == "required" || askForAddressTransportationSetting == "required") {
            askForAddress = "required";
          } else if (askForAddress === true) {
            askForAddress = "optional";
          } else if (askForAddress === false) {
            askForAddress = "no";
          }

          const displayMobileServiceAddress = askForAddress != "no" && (formProps.values.concern && formProps.values.concernDetail);
          const addressRequired = askForAddress == "required";
          const addressEntered = (
            formProps.values.address
              && formProps.values.city && !formProps.errors.city
              && formProps.values.state && !formProps.errors.state
              && formProps.values.postal_code && !formProps.errors.postal_code
          )

          const hideAdvisorSelection = (
            window.gon.shoploader_lite &&
            props.advisors && Object.keys(props.advisors).length == 1 &&
            Object.values(props.advisors)[0] && Object.values(props.advisors)[0].attributes.full_name == 'First Available'
          ) || (
            addressRequired && !addressEntered
          );

          const displayAdvisorSelection = !hideAdvisorSelection;
          const displayAppointmentDayTime = formProps.values.concern &&
                                            formProps.values.concernDetail &&
                                            formProps.values.advisor &&
                                            Object.keys(props.appointments(formProps.values.advisor)).length >= 0 &&
                                            ((displayMobileServiceAddress && addressEntered) || askForAddress != "required");

          // Utilities used in dynamically applying schema validation
          const containsAll = (arr1, arr2) => arr2.every(arr2Item => arr1.includes(arr2Item))
          const sameMembers = (arr1, arr2) => containsAll(arr1, arr2) && containsAll(arr2, arr1);
          const determineRequiredFields = (validationSchema) => {
            let resultArray = [];
            let fields = validationSchema?.fields || {};

            for (let prop in fields) {
              let hasRequiredValidation = false;

              // Check for a required validation check on the field
              hasRequiredValidation = fields[prop].tests.find((f) => {
                return f.OPTIONS['name'] == 'required'
              }) != undefined;

              if (hasRequiredValidation) {
                resultArray.push(prop);
              }
            }

            return resultArray;
          }

          // Apply schema validations based on settings or chosen skill type
          useEffect(() => {
            // Begin using default validation schema
            let finalValidationSchema = emptySchemaField.concat(defaultValidationSchema);

            // Add primary concern description validation if it is required
            if(primaryConcernDescriptionRequired) {
              finalValidationSchema = finalValidationSchema.concat(requiredPrimaryConcernDescriptionValidationSchema);
            }

            // Add findPhoneNumber validation is Find Vehicle panel is enabled
            if(findPhoneRequired) {
              finalValidationSchema = finalValidationSchema.concat(findVehicleValidationSchema);
            }

            // Add VIN validation if VIN is required
            if(vinRequired) {
              finalValidationSchema = finalValidationSchema.concat(requiredVinValidationSchema);
            }

            // Add mileage validation if mileage is required
            if(mileageRequired) {
              finalValidationSchema = finalValidationSchema.concat(requiredMileageValidationSchema);
            }

            // Add address validations if address is required
            if(addressRequired) {
              finalValidationSchema = finalValidationSchema.concat(requiredAddressValidationSchema);
            }

            // Add opt-in consent validation if text messaging is enabled or we've forced opt-in consent
            // requirement while the text messaging feature is awaiting campaign review.
            if(requireOptInConsent) {
              finalValidationSchema = finalValidationSchema.concat(requiredOptInValidationSchema);
            }

            let oldRequired = determineRequiredFields(validationSchema);
            let newRequired = determineRequiredFields(finalValidationSchema);
            let validationChanged = !sameMembers(oldRequired, newRequired);

            // Update schema validation if it changed from last render
            // For instance, they chose a skill that requires VIN
            if(validationChanged) {
              setValidationSchema(finalValidationSchema);
            }
          }, [formProps, validationSchema])

          return (
            <Form onSubmit={formProps.handleSubmit}>
              {
                props.concernIdsSorted.length > 0 &&
                <Accordion defaultActiveKey="Concern">
                  <Card>
                    <Card.Header>
                      <Row xs={1} sm={1} md={2} lg={2}>
                       <Col>
                          <Accordion.Toggle as={Button} eventKey="Concern" variant="dark">
                            Primary Service Needed
                          </Accordion.Toggle>
                        </Col>
                        {
                          props.concerns[formProps.values.concern] &&
                          props.concerns[formProps.values.concern].attributes ?
                            <Col>
                              {props.concerns[formProps.values.concern].attributes.name}
                              {props.concerns[formProps.values.concern].attributes.online_details_link_url && (
                                <a href={props.concerns[formProps.values.concern].attributes.online_details_link_url} className="concern-details-link" target="_blank">
                                  {props.concerns[formProps.values.concern].attributes.online_details_link_text || "Click here for more details"}
                                </a>)}
                            </Col> :
                            <Col>
                              What is your primary service need?
                            </Col>
                        }
                      </Row>
                    </Card.Header>
                    <Accordion.Collapse eventKey="Concern">
                      <Card.Body>
                        <Row xs={1} md={1} lg={1}>
                          {
                            props.concernIdsSorted.map(
                              concern => (
                                <Col key={`concern_${props.concerns[concern].id}`} >
                                  <Accordion.Toggle
                                    as={Button}
                                    variant={ formProps.values.concern === props.concerns[concern].id ? 'dark' : 'outline-dark' }
                                    onClick={ () => { _handleChangeConcern(props.concerns[concern].id); } }
                                    eventKey="ConcernDetail"
                                    className="optionButton"
                                  >
                                    {props.concerns[concern].attributes.name}
                                  </Accordion.Toggle>
                                </Col>
                              )
                            )
                          }
                        </Row>
                      </Card.Body>
                    </Accordion.Collapse>
                  </Card>
                  {
                    formProps.values.concern &&
                    <Card>
                      <Card.Header>
                        <Row xs={1} sm={1} md={2} lg={2}>
                          <Col>
                            <Accordion.Toggle as={Button} eventKey="ConcernDetail" variant="dark">
                              Service Need Detail
                            </Accordion.Toggle>
                          </Col>
                          {
                            formProps.values.concernDetail ?
                              <Col>
                                {formProps.values.concernDetail}
                              </Col> :
                              <Col>
                                What is the detail of your service need?
                              </Col>
                          }
                        </Row>
                      </Card.Header>
                      <Accordion.Collapse eventKey="ConcernDetail">
                        <Card.Body>
                         {
                            formProps.values.concern &&
                            <Row xs={1} md={1} lg={1}>
                              {
                                props.concerns[String(formProps.values.concern)].attributes.detailed_vehicle_concerns.map(
                                  concernDetail =>
                                    <Col key={concernDetail} >
                                      <Accordion.Toggle
                                        as={Button}
                                        variant={ formProps.values.concernDetail === concernDetail ? 'dark' : 'outline-dark' }
                                        onClick={ () => {
                                          formProps.setFieldValue('concernDetail', concernDetail);
                                          formProps.setFieldValue('concernDescription', props.concerns[String(formProps.values.concern)].attributes.name + ' - ' + concernDetail);

                                          // If the primary concern description is required, then mark it as touched
                                          // and immediately validate the form so that it becomes visually apparent
                                          // the field is required using the red border on the text area.
                                          if (primaryConcernDescriptionRequired) {
                                            formProps.setFieldTouched('concernDescriptionAdditionalDetails', true);
                                            formProps.validateForm();
                                            // Allow animation to complete then focus on the text area
                                            setTimeout(() => {
                                              document.querySelector('#concernDescription')?.focus();
                                            }, 200);
                                          }
                                        } }
                                        eventKey="ConcernDescription"
                                        className="optionButton"
                                      >
                                        {concernDetail}
                                      </Accordion.Toggle>
                                    </Col>
                                )
                              }
                            </Row>
                         }
                        </Card.Body>
                      </Accordion.Collapse>
                    </Card>
                  }

                  {
                    formProps.values.concernDetail &&
                    <Card>
                      <Card.Header>
                        <Row xs={1} sm={1} md={2} lg={2}>
                          <Col>
                            <Accordion.Toggle as={Button} eventKey="ConcernDescription" variant="dark">
                              Describe Service Needed
                            </Accordion.Toggle>
                          </Col>
                          {
                            (
                              (formProps.values.concernDescriptionAdditionalDetails && formProps.values.concernDescriptionAdditionalDetails.length > 1) ||
                              formProps.values.additionalConcerns ||
                              formProps.values.transportationRequest == 'rentalRequested' ||
                              formProps.values.transportationRequest == 'shuttleRequested' ||
                              formProps.values.transportationRequest == 'pickupRequested' ||
                              formProps.values.transportationRequest == 'waiter' ||
                              formProps.values.transportationRequest == 'selfDropRequested' ||
                              formProps.values.transportationRequest == 'nightDropRequested'
                            ) ?
                              <Col>
                                <pre>{formProps.values.concernDescriptionAdditionalDetails}</pre>
                                {formProps.values.additionalConcerns}<br/>
                                {formProps.values.transportationRequest == 'rentalRequested' ? <>&#x2713; {props.organization.rental_requested_label}<br/></> : '' }
                                {formProps.values.transportationRequest == 'shuttleRequested' ? <>&#x2713; {props.organization.shuttle_requested_label}<br/></> : '' }
                                {formProps.values.transportationRequest == 'pickupRequested' ? <>&#x2713; {props.organization.pickup_requested_label}<br/></> : '' }
                                {formProps.values.transportationRequest == 'waiter' ? <>&#x2713; {props.organization.waiter_label}<br/></> : '' }
                                {formProps.values.transportationRequest == 'selfDropRequested' ? <>&#x2713; {props.organization.self_drop_label}<br/></> : '' }
                                {formProps.values.transportationRequest == 'nightDropRequested' ? <>&#x2713; {props.organization.night_drop_label}</> : '' }
                              </Col> :
                              <Col>
                                Further describe your service need if necessary.
                                {displayAdvisorSelection && (<>&nbsp;Click the "Advisor" button to proceed.</>)}
                              </Col>
                          }
                        </Row>
                      </Card.Header>
                      <Accordion.Collapse eventKey="ConcernDescription">
                        <Card.Body>
                          <Form.Row>
                            <Form.Group as={Col} controlId="concernDescription">
                              <Form.Label>Primary Concern Description / Service Needed: {formProps.values.concernDescription}</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows="2"
                                placeholder={primaryConcernDescriptionPlaceholder}
                                name="concernDescriptionAdditionalDetails"
                                maxLength="250"
                                onChange={formProps.handleChange}
                                onBlur={formProps.handleBlur}
                                value={formProps.values.concernDescriptionAdditionalDetails}
                                isInvalid={formProps.touched.concernDescriptionAdditionalDetails && !!formProps.errors.concernDescriptionAdditionalDetails}
                                isValid={formProps.touched.concernDescriptionAdditionalDetails && !formProps.errors.concernDescriptionAdditionalDetails}
                              />
                              <Form.Text className="text-muted">
                                {`${250 - (formProps.values.concernDescriptionAdditionalDetails || "").length} characters remaining.`}
                              </Form.Text>
                              <Form.Control.Feedback type="invalid">
                                {formProps.errors.concernDescriptionAdditionalDetails}
                              </Form.Control.Feedback>
                              {fetchingSkillClassifications && (
                                <>
                                  <div className="skill-classification-options">
                                    <h5>Thank you for your description!</h5>
                                    <div>
                                      Attempting to verify and match your skill based on description provided...
                                      <Spinner animation="grow" size="sm" />
                                    </div>
                                  </div>
                                </>
                              )}
                              {!fetchingSkillClassifications && matchedSkillClassification && (
                                <>
                                  <div className="skill-classification-options">
                                    <h5>Great Work! 🎉</h5>
                                    <p>
                                      Your description sounds like a match for the chosen concern <strong>{props.concerns[formProps.values.concern].attributes.name}</strong>.<br />
                                      Getting this right helps us identify the best appointment time for you.<br />
                                      Click <Accordion.Toggle
                                        as={Button}
                                        onClick={() => {
                                          setFetchingSkillClassifications(false);
                                          setMatchedSkillClassification(false);
                                          formProps.setFieldValue('potentiallyMatchingSkills', []);
                                        }}
                                        variant="link"
                                        eventKey="Advisors"
                                        disabled={formProps.errors.additionalConcerns || formProps.errors.concernDescriptionAdditionalDetails}
                                      >
                                        Done adding additional details
                                      </Accordion.Toggle> to keep going.
                                    </p>
                                  </div>
                                </>
                              )}
                              {formProps.values.potentiallyMatchingSkills.length > 0 && (
                                <>
                                  <div className="skill-classification-options">
                                    <h5>{(formProps.values.potentiallyMatchingSkills.length > 1) ? 'Do any of these' : 'Does this'} better match your vehicle concern?</h5>
                                    <p>Based on your description, {(formProps.values.potentiallyMatchingSkills.length > 1) ? 'one of these choices' : 'this choice' } might be a better match:</p>
                                    <ul className="potentiallyMatchingSkills">
                                      {formProps.values.potentiallyMatchingSkills.map(skill =>
                                        (
                                          <li key={skill.id}>
                                            <Accordion.Toggle
                                              as={Button}
                                              onClick={() => {
                                                setFetchingSkillClassifications(false);
                                                setMatchedSkillClassification(false);
                                                formProps.setFieldValue('potentiallyMatchingSkills', []);
                                                _handleChangeConcern(skill.id, formProps.values.concernDescriptionAdditionalDetails);
                                                // Remove the previously selected primary skill and service need detail from the description
                                                // This will make the final primary concern description consist of only the user's typed input
                                                formProps.setFieldValue('concernDescription', '');
                                              }}
                                              variant="outline-dark"
                                              eventKey="Advisors"
                                              disabled={formProps.errors.additionalConcerns || formProps.errors.concernDescriptionAdditionalDetails}
                                            >
                                              {skill.name}
                                            </Accordion.Toggle>
                                          </li>
                                        )
                                      )}
                                    </ul>
                                  </div>
                                </>
                              )}
                              {!fetchingSkillClassifications && failedMatchedSkillClassification && (
                                <>
                                  <div className="skill-classification-options">
                                    <h5>Sorry, we didn't find a match</h5>
                                    <p>
                                      We could't identify a match based on your description.
                                      Click <Accordion.Toggle
                                        as={Button}
                                        onClick={() => {
                                          setFetchingSkillClassifications(false);
                                          setMatchedSkillClassification(false);
                                          formProps.setFieldValue('potentiallyMatchingSkills', []);
                                        }}
                                        variant="link"
                                        eventKey="Advisors"
                                        disabled={formProps.errors.additionalConcerns || formProps.errors.concernDescriptionAdditionalDetails}
                                      >
                                        Done adding additional details
                                      </Accordion.Toggle> if you're confident in your selection.<br />
                                      You can try rephrasing and we'll help you find the right concern selection.
                                      Getting this right helps us identify the best appointment time for you.
                                    </p>
                                  </div>
                                </>
                              )}
                            </Form.Group>
                          </Form.Row>
                          {props.organization.ask_for_additional_concerns_online != "no" && (
                            <Form.Row>
                              <Form.Group as={Col} controlId="additionalConcerns">
                                <Form.Label>Additional Concerns and Requests</Form.Label>
                                <Form.Control
                                  as="textarea"
                                  rows="2"
                                  placeholder="If you have additional vehicle concerns or service requests enter them here. (optional)"
                                  name="additionalConcerns"
                                  maxLength="250"
                                  onChange={formProps.handleChange}
                                  onBlur={formProps.handleBlur}
                                  value={formProps.values.additionalConcerns}
                                  isInvalid={formProps.touched.additionalConcerns && !!formProps.errors.additionalConcerns}
                                  isValid={formProps.touched.additionalConcerns && !formProps.errors.additionalConcerns}
                                />
                                <Form.Text className="text-muted">
                                  {`${250 - formProps.values.additionalConcerns.length} characters remaining.`}
                                </Form.Text>
                                <Form.Control.Feedback type="invalid">
                                  {formProps.errors.additionalConcerns}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Form.Row>
                          )}
                          {props.organization.display_rental_requested_customers && !isMobileConcern && <Form.Row id="rentalRequestedRow">
                            <Form.Group as={Col} controlId="rentalRequested">
                              <Form.Check
                                type="radio"
                                name="transportationRequest"
                                id="rentalRequested"
                                value="rentalRequested"
                                label={props.organization.rental_requested_label}
                                checked={formProps.values.transportationRequest == 'rentalRequested'}
                                onClick={ (e) => { _handleChangeTransportationRequest(e.target.value); } }
                              />
                              {props.organization.rental_requested_description && formProps.values.transportationRequest == 'rentalRequested' && (
                                <div className="transportationLabelDescription rentalRequestedDescription"><p>{props.organization.rental_requested_description}</p></div>
                              )}
                            </Form.Group>
                          </Form.Row>}
                          {props.organization.display_shuttle_requested_customers && !isMobileConcern && <Form.Row id="shuttleRequestedRow">
                            <Form.Group as={Col} controlId="shuttleRequested">
                              <Form.Check
                                type="radio"
                                name="transportationRequest"
                                id="shuttleRequested"
                                value="shuttleRequested"
                                label={props.organization.shuttle_requested_label}
                                checked={formProps.values.transportationRequest == 'shuttleRequested'}
                                onClick={ (e) => { _handleChangeTransportationRequest(e.target.value); } }
                              />
                              {props.organization.shuttle_requested_description && formProps.values.transportationRequest == 'shuttleRequested' && (
                                <div className="transportationLabelDescription shuttleRequestedDescription"><p>{props.organization.shuttle_requested_description}</p></div>
                              )}
                            </Form.Group>
                          </Form.Row>}
                          {props.organization.display_pickup_requested_customers && !isMobileConcern && <Form.Row id="pickupRequestedRow">
                            <Form.Group as={Col} controlId="pickupRequested">
                              <Form.Check
                                type="radio"
                                name="transportationRequest"
                                id="pickupRequested"
                                value="pickupRequested"
                                label={props.organization.pickup_requested_label}
                                checked={formProps.values.transportationRequest == 'pickupRequested'}
                                onClick={ (e) => { _handleChangeTransportationRequest(e.target.value); } }
                              />
                              {props.organization.pickup_requested_description && formProps.values.transportationRequest == 'pickupRequested' && (
                                <div className="transportationLabelDescription pickupRequestedDescription"><p>{props.organization.pickup_requested_description}</p></div>
                              )}
                            </Form.Group>
                          </Form.Row>}
                          {props.organization.display_waiter_customers && !isMobileConcern && <Form.Row id="waiterRow">
                            <Form.Group as={Col} controlId="waiter">
                              <Form.Check
                                type="radio"
                                name="transportationRequest"
                                id="waiter"
                                value="waiter"
                                label={props.organization.waiter_label}
                                checked={formProps.values.transportationRequest == 'waiter'}
                                onClick={ (e) => { _handleChangeTransportationRequest(e.target.value); } }
                              />
                              {props.organization.waiter_description && formProps.values.transportationRequest == 'waiter' && (
                                <div className="transportationLabelDescription waiterDescription"><p>{props.organization.waiter_description}</p></div>
                              )}
                            </Form.Group>
                          </Form.Row>}
                          {props.organization.display_self_drop_requested_customers && !isMobileConcern && <Form.Row id="selfDropRequestedRow">
                            <Form.Group as={Col} controlId="selfDropRequested">
                              <Form.Check
                                type="radio"
                                name="transportationRequest"
                                id="selfDropRequested"
                                value="selfDropRequested"
                                label={props.organization.self_drop_label}
                                checked={formProps.values.transportationRequest == 'selfDropRequested'}
                                onClick={ (e) => { _handleChangeTransportationRequest(e.target.value); } }
                              />
                              {props.organization.self_drop_description && formProps.values.transportationRequest == 'selfDropRequested' && (
                                <div className="transportationLabelDescription selfDropDescription"><p>{props.organization.self_drop_description}</p></div>
                              )}
                            </Form.Group>
                          </Form.Row>}
                          {props.organization.display_night_drop_requested_customers && !isMobileConcern && <Form.Row id="nightDropRequestedRow">
                            <Form.Group as={Col} controlId="nightDropRequested">
                              <Form.Check
                                type="radio"
                                name="transportationRequest"
                                id="nightDropRequested"
                                value="nightDropRequested"
                                label={props.organization.night_drop_label}
                                checked={formProps.values.transportationRequest == 'nightDropRequested'}
                                onClick={ (e) => { _handleChangeTransportationRequest(e.target.value); } }
                              />
                              {props.organization.night_drop_description && formProps.values.transportationRequest == 'nightDropRequested' && (
                                <div className="transportationLabelDescription nightDropDescription"><p>{props.organization.night_drop_description}</p></div>
                              )}
                            </Form.Group>
                          </Form.Row>}
                          {!displayMobileServiceAddress && displayAdvisorSelection && <Row>
                            <Col>
                              <Accordion.Toggle
                                as={Button}
                                eventKey="Advisors"
                                variant="dark"
                                disabled={formProps.errors.additionalConcerns || formProps.errors.concernDescriptionAdditionalDetails}
                              >
                                Done adding additional details
                              </Accordion.Toggle>
                            </Col>
                          </Row>}
                          {displayMobileServiceAddress && !(formProps.errors.additionalConcerns || formProps.errors.concernDescription) && <Row>
                            <Col>
                              <Accordion.Toggle
                                as={Button}
                                eventKey="Address"
                                variant="dark"
                                disabled={formProps.errors.additionalConcerns || formProps.errors.concernDescriptionAdditionalDetails}
                              >
                                Next
                              </Accordion.Toggle>
                            </Col>
                          </Row>}
                        </Card.Body>
                      </Accordion.Collapse>
                    </Card>
                  }

                  {
                    displayMobileServiceAddress &&
                    <Card>
                      <Card.Header>
                        <Row xs={1} sm={1} md={2} lg={2}>
                          <Col>
                            <Accordion.Toggle as={Button} eventKey="Address" variant="dark" disabled={formProps.errors.additionalConcerns || formProps.errors.concernDescriptionAdditionalDetails}>
                              Vehicle Location
                            </Accordion.Toggle>
                          </Col>
                            {
                              formProps.values.address ?
                                <Col>
                                  {formProps.values.address}
                                  {formProps.values.suite &&
                                      ` ${formProps.values.suite}`
                                  }
                                  <br/>
                                  {
                                    formProps.values.city &&
                                      `${formProps.values.city}`
                                  }
                                  {
                                    formProps.values.state &&
                                      ` ${formProps.values.state}`
                                  }
                                  {
                                    formProps.values.postal_code &&
                                      ` ${formProps.values.postal_code}`
                                  }
                                  {
                                    formProps.values.special_instructions &&
                                      (
                                        <>
                                          <br/>{formProps.values.special_instructions}
                                        </>
                                      )
                                  }
                                </Col> :
                                <Col>
                                  Please provide the address where your vehicle will be located.
                                </Col>
                            }
                        </Row>
                      </Card.Header>
                      <Accordion.Collapse eventKey="Address">
                        <Card.Body>
                          <Form.Row>
                            <Form.Group as={Col} xs={12} sm={6} md={6} lg={4} controlId="address">
                              <Form.Label>Address</Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Address"
                                name="address"
                                onChange={formProps.handleChange}
                                onBlur={formProps.handleBlur}
                                value={formProps.values.address}
                                isInvalid={formProps.touched.address && !!formProps.errors.address}
                                isValid={formProps.touched.address && !formProps.errors.address}
                              />
                              <Form.Control.Feedback type="invalid">
                                {formProps.errors.address}
                              </Form.Control.Feedback>
                              <Form.Text className="text-muted">
                                Enter the address where your vehicle will be located.
                              </Form.Text>
                            </Form.Group>
                            <Form.Group as={Col} xs={7} sm={6} md={3} lg={2} controlId="suite">
                              <Form.Label>Suite</Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Suite"
                                name="suite"
                                onChange={formProps.handleChange}
                                onBlur={formProps.handleBlur}
                                value={formProps.values.suite}
                                isInvalid={formProps.touched.suite && !!formProps.errors.suite}
                                isValid={formProps.touched.suite && !formProps.errors.suite}
                              />
                              <Form.Control.Feedback type="invalid">
                                {formProps.errors.suite}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Form.Row>
                          <Form.Row>
                            <Form.Group as={Col} xs={7} sm={6} md={3} lg={4} controlId="city">
                              <Form.Label>City</Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="City"
                                name="city"
                                onChange={formProps.handleChange}
                                onBlur={formProps.handleBlur}
                                value={formProps.values.city}
                                isInvalid={formProps.touched.city && !!formProps.errors.city}
                                isValid={formProps.touched.city && !formProps.errors.city}
                              />
                              <Form.Control.Feedback type="invalid">
                                {formProps.errors.city}
                              </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group as={Col} xs={12} sm={6} md={6} lg={4} controlId="state">
                              <Form.Label>State</Form.Label>
                              <Form.Control
                                as="select"
                                type="state"
                                placeholder="State"
                                name="state"
                                onChange={formProps.handleChange}
                                onBlur={formProps.handleBlur}
                                value={formProps.values.state}
                                isInvalid={formProps.touched.state && !!formProps.errors.state}
                                isValid={formProps.touched.state && !formProps.errors.state}
                              >
                                {stateSelectOptions()}
                              </Form.Control>
                              <Form.Control.Feedback type="invalid">
                                {formProps.errors.state}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Form.Row>
                          <Form.Row>
                            <Form.Group as={Col} xs={7} sm={6} md={3} lg={4} controlId="postal_code">
                              <Form.Label>Postal Code</Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Postal Code"
                                name="postal_code"
                                onChange={formProps.handleChange}
                                onBlur={formProps.handleBlur}
                                value={formProps.values.postal_code}
                                isInvalid={formProps.touched.postal_code && !!formProps.errors.postal_code}
                                isValid={formProps.touched.postal_code && !formProps.errors.postal_code}
                              />
                              <Form.Control.Feedback type="invalid">
                                {formProps.errors.postal_code}
                              </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group as={Col} xs={12} sm={6} md={6} lg={4} controlId="special_instructions">
                              <Form.Label>Special Instructions</Form.Label>
                              <Form.Control
                                name="special_instructions"
                                maxLength="100"
                                type="text"
                                placeholder="Special Instructions"
                                onChange={formProps.handleChange}
                                onBlur={formProps.handleBlur}
                                value={formProps.values.special_instructions}
                                isInvalid={formProps.touched.special_instructions && !!formProps.errors.special_instructions}
                                isValid={formProps.touched.special_instructions && !formProps.errors.special_instructions}
                              />
                              <Form.Control.Feedback type="invalid">
                                {formProps.errors.special_instructions}
                              </Form.Control.Feedback>
                              <Form.Text className="text-muted">
                                Example: Entry instructions, gate code, access code or other information needed to visit your vehicle location.
                              </Form.Text>
                            </Form.Group>
                          </Form.Row>
                          {displayMobileServiceAddress && <Row>
                            <Col>
                              <Accordion.Toggle
                                as={Button}
                                eventKey="Advisors"
                                variant="dark"
                                disabled={!addressEntered && askForAddress == "required"}
                              >
                                Next
                              </Accordion.Toggle>
                            </Col>
                          </Row>}
                        </Card.Body>
                      </Accordion.Collapse>
                    </Card>
                  }

                  {
                    formProps.values.concern && formProps.values.concernDetail &&
                    displayAdvisorSelection &&
                    <Card>
                      <Card.Header>
                        <Row xs={1} sm={1} md={2} lg={2}>
                          <Col>
                            <Accordion.Toggle
                              as={Button}
                              eventKey="Advisors"
                              variant="dark"
                              disabled={formProps.errors.additionalConcerns || formProps.errors.concernDescriptionAdditionalDetails}
                            >
                              Advisor
                            </Accordion.Toggle>
                          </Col>
                          {
                            formProps.values.advisor && props.advisors[formProps.values.advisor] ?
                              <Col>
                                {` ${props.advisors[formProps.values.advisor].attributes.full_name}`}
                              </Col> :
                              <Col>
                                Please choose your advisor.
                              </Col>
                          }
                        </Row>
                      </Card.Header>
                      <Accordion.Collapse eventKey="Advisors">
                        <Card.Body>
                          {
                            formProps.values.concern &&
                            <Row xs={1} md={2} lg={3}>
                              {
                                Object.keys(props.advisors).map(
                                  advisor =>
                                    <Col key={props.advisors[advisor].id} >
                                      <Accordion.Toggle
                                        as={Button}
                                        variant={ formProps.values.advisor === props.advisors[advisor].id ? 'dark' : 'outline-dark' }
                                        onClick={ () => { _handleChangeAdvisor(props.advisors[advisor].id); } }
                                        eventKey="Appointments"
                                        className="optionButton"
                                      >
                                        {props.advisors[advisor].attributes.full_name}
                                      </Accordion.Toggle>
                                    </Col>
                                )
                              }
                            </Row>
                          }
                        </Card.Body>
                      </Accordion.Collapse>
                    </Card>
                  }

                  {displayAppointmentDayTime &&
                    <Card>
                      <Card.Header>
                        <Row xs={1} sm={1} md={2} lg={2}>
                          <Col>
                            <Accordion.Toggle as={Button} eventKey="Appointments" variant="dark">
                              Appointment Day/Time
                            </Accordion.Toggle>
                          </Col>
                            {
                              appointmentTime ?
                                <Col>
                                  {formattedChosenAvailability(props.concerns[formProps.values.concern].attributes, appointmentTime)}<br/>
                                  {
                                    appointmentAdvisor && displayAdvisorSelection &&
                                    `Advisor: ${appointmentAdvisor}`
                                  }
                                </Col> :
                                <Col>
                                  Please select your appointment time.
                                </Col>
                            }
                        </Row>
                      </Card.Header>
                      <Accordion.Collapse eventKey="Appointments">
                        <Card.Body>
                          <Card>
                            <Accordion>
                              <Card.Header>
                                <Row xs={1} sm={1} md={2} lg={2}>
                                  <Col>
                                    <Accordion.Toggle as={Button} eventKey="Calendar" variant="dark">
                                      Choose Another Date
                                    </Accordion.Toggle>
                                  </Col>
                                  <Col>

                                  </Col>
                                </Row>
                              </Card.Header>
                              <Accordion.Collapse eventKey="Calendar">
                                <Card.Body>
                                  <Calendar
                                    selectRange={false}
                                    value={appointmentDate}
                                    onChange={_handleDateChange}
                                    maxDate={maxDate}
                                    minDate={props.minDate}
                                    formatMonthYear={(locale, date) => date.toLocaleString(locale, { month: 'short', year: 'numeric' })}
                                  />
                                </Card.Body>
                              </Accordion.Collapse>
                            </Accordion>
                          </Card>
                          <div className="week-calendar">
                            <Row xs={3} sm={3} md={3} lg={3} className="daynames">
                              <Col
                                className={`selected_date selected_date_left ${ _hasPriorDate ? '' : 'selected_date_disabled' }`}
                                onClick={_handlePreviousDate}
                              >
                                &lt;
                              </Col>
                              <Col className="selected_date selected_date_center">
                                {moment(appointmentDate).format('ddd')}
                                <br/>
                                {moment(appointmentDate).format('MMM Do')}
                              </Col>
                              <Col
                                className={`selected_date selected_date_right ${ _hasNextDate ? '' : 'selected_date_disabled' }`}
                                onClick={_handleNextDate}
                              >
                                &gt;
                              </Col>
                            </Row>
                            {
                              !appointmentSearchInProgress && appointments.length > 0 ?
                                appointments.map( (date, i) => (
                                  <Row xs={1} sm={1} md={1} lg={1} key={i}>
                                    {
                                      date.map( (time, j) => (
                                        time.available && <Accordion.Toggle
                                          as={Button}
                                          eventKey={((vehicleYearMakeModelEntered) ? (!((vinRequired && !formProps.values.vin) || (mileageRequired && !formProps.values.mileage)) ? "Contact" : "Vehicle") : (findVehicleEnabled ? "FindVehicle" : "Vehicle"))}
                                          variant={ time.id === formProps.values.appointment ? "dark" : "outline-dark" }
                                          className={`${
                                            time.id === formProps.values.appointment ? ' selected' : ''
                                          }`}
                                          data-datetime={time.moment.format()}
                                          key={j}
                                          onClick={ () => { _handleSelectAppointment(time); } }
                                        >
                                          {availabilityOptionText(props.concerns[formProps.values.concern].attributes, time, displayAdvisorSelection)}
                                        </Accordion.Toggle>
                                      ) )
                                    }
                                  </Row>
                                ) ) :
                                (
                                  appointmentSearchInProgress ? (
                                    <Row xs={1} sm={1} md={1} lg={1} key={0}>
                                      <Accordion.Toggle as={Button} disabled={true} variant="outline-dark" className="unavailable">
                                        <br /><br />
                                        <Spinner animation="grow" size="lg" />
                                        <br /><br />
                                        <h4>Finding Available Appointments</h4>
                                        <p>Please wait while we find available times for your service appointment.</p>
                                        <br /><br />
                                      </Accordion.Toggle>
                                    </Row>
                                  ) :
                                  (
                                    <Row xs={1} sm={1} md={1} lg={1} key={0}>
                                      <Accordion.Toggle as={Button} disabled={true} variant="outline-dark" className="unavailable">
                                        No Appointments Available on this Day
                                      </Accordion.Toggle>
                                    </Row>
                                  )
                                )
                            }
                            { !formProps.errors.appointment &&
                              <Form.Control.Feedback type="invalid">
                                {formProps.errors.appointment}
                              </Form.Control.Feedback>
                            }
                          </div>
                        </Card.Body>
                      </Accordion.Collapse>
                    </Card>
                  }

                  {
                    showFindVehicle &&
                    <Card>
                      <Card.Header>
                        <Row xs={1} sm={1} md={2} lg={2}>
                          <Col>
                            <Accordion.Toggle as={Button} eventKey="FindVehicle" variant="dark">
                              Search For Vehicle
                            </Accordion.Toggle>
                          </Col>
                        </Row>
                      </Card.Header>
                      <Accordion.Collapse eventKey="FindVehicle">
                        <Card.Body>
                          <div className="find-vehicle">
                            <p>Enter your mobile number to search for your vehicle.</p>
                            <div className="find-vehicle-field">
                              <Form.Label>Mobile Number</Form.Label>
                              <MaskedInput
                                name="findPhoneNumber"
                                mask="(000) 000-0000"
                                lazy={false}
                                onAccept={(value) => {
                                  formProps.setFieldValue('findPhoneNumber', value);
                                  formProps.setFieldValue('phoneNumber', value)
                                  formProps.setFieldValue('findEmailAddress', '');
                                  formProps.setFieldValue('email', '');
                                  formProps.setFieldValue('verificationToken', null);
                                  setVehicleSearchResultCount(null);
                                  setVehicleSearchNoVehicleFound(false);
                                }}
                                onKeyPress={(target) => {
                                  if (!!formProps.errors.findPhoneNumber || vehicleSearchInProgress) {
                                    return;
                                  }

                                  if (target.charCode == 13) {
                                    _handleFindVehicle();
                                    return;
                                  }
                                }}
                                onBlur={formProps.handleBlur}
                                value={formProps.values.findPhoneNumber}
                                isInvalid={formProps.touched.findPhoneNumber && !!formProps.errors.findPhoneNumber}
                                isValid={formProps.touched.findPhoneNumber && !formProps.errors.findPhoneNumber}
                                inputMode="numeric"
                                data-lp-ignore="true"
                              />
                              <Button
                               variant="dark"
                               disabled={!!formProps.errors.findPhoneNumber || vehicleSearchInProgress}
                               onClick={!vehicleSearchInProgress ? () => {
                                 // Clear the confirmation results so we do a fresh confirm flow when phone was changed or resubmit.
                                 _resetConfirmationVerification();
                                 _handleFindVehicle();
                               } : null}>
                                Find
                              </Button>
                              {vehicleSearchInProgress && <Spinner animation="grow" size="sm" />}
                              <Form.Control.Feedback type="invalid">
                                <p>{formProps.errors.findPhoneNumber}</p>
                              </Form.Control.Feedback>
                              {!formProps.errors.findPhoneNumber && vehicleSearchNoVehicleFound && (
                                <Form.Text>
                                  <p className="text-danger">
                                    {vehicleSearchErrorMessage === null && !showFindVehicleEmailAddress && defaultVehicleSearchErrorMessage('Sorry, no vehicle found. Enter another mobile phone number')}
                                    {vehicleSearchErrorMessage !== null && vehicleSearchErrorMessage}
                                  </p>
                                </Form.Text>
                              )}
                            </div>
                            <p className="find-vehicle-disclaimer">
                              We will only use your mobile number to search and send
                              text reminders and messages regarding your service.
                            </p>
                            <p className="find-vehicle-disclaimer">
                              By entering my mobile number and clicking Find,
                              I agree to receive confirmation code via text message.
                            </p>
                            {showFindVehicleEmailAddress && (
                              <div className="find-vehicle-field find-email-address-field">
                                <Alert variant="warning">
                                  This number matches more than one customer. To guide us to the right person, please provide your email address.
                                </Alert>
                                <div className="find-email-address-controls">
                                  <Form.Label>Email Address</Form.Label>
                                  <Form.Control
                                    name="findEmailAddress"
                                    maxLength="320"
                                    onKeyPress={(target) => {
                                      if (!!formProps.errors.findEmailAddress || vehicleSearchInProgress) {
                                        return;
                                      }

                                      if (target.charCode == 13) {
                                        _handleFindVehicle();
                                        return;
                                      }
                                    }}
                                    onChange={(e) => {
                                      formProps.setFieldValue('findEmailAddress', e.target.value);
                                      formProps.setFieldValue('email', e.target.value)
                                      formProps.setFieldValue('verificationToken', null);
                                      setVehicleSearchNoVehicleFound(false);
                                    }}
                                    onBlur={formProps.handleBlur}
                                    value={formProps.values.findEmailAddress}
                                    isInvalid={formProps.touched.findEmailAddress && !!formProps.errors.findEmailAddress}
                                    isValid={formProps.touched.findEmailAddress && !formProps.errors.findEmailAddress}
                                    data-lp-ignore="true"
                                  />
                                  <Button
                                    variant="dark"
                                    disabled={!!formProps.errors.findEmailAddress || vehicleSearchInProgress}
                                    onClick={!vehicleSearchInProgress ? () => {
                                      // Clear the token so we do a fresh confirm flow when email was changed or resubmit.
                                      _resetConfirmationVerification();
                                      _handleFindVehicle();
                                    } : null}>
                                    Find
                                  </Button>
                                </div>
                                {vehicleSearchInProgress && <Spinner animation="grow" size="sm" />}
                                <Form.Control.Feedback type="invalid">
                                  <p>{formProps.errors.findEmailAddress}</p>
                                </Form.Control.Feedback>
                                {!formProps.errors.findEmailAddress && vehicleSearchNoVehicleFound && (
                                  <Form.Text>
                                    <p className="text-danger">
                                      {vehicleSearchErrorMessage === null && defaultVehicleSearchErrorMessage('Sorry, no match found. Enter another email address')}
                                      {vehicleSearchErrorMessage !== null && vehicleSearchErrorMessage}
                                    </p>
                                  </Form.Text>
                                )}
                              </div>
                            )}
                            {!vehicleSearchErrorMessage && !vehicleSearchNoVehicleFound && showVerificationConfirmationControls && (
                              <div className="find-vehicle-field verification-confirmation-field">
                                {!confirmationCodeSuccessMessage && (
                                  <Alert variant="info">
                                    We just sent you a confirmation code. Please enter the confirmation code below to continue.
                                  </Alert>
                                )}
                                {confirmationCodeSuccessMessage && (
                                  <Alert variant="success">
                                    {confirmationCodeSuccessMessage}
                                    &nbsp;Searching vehicles. Please choose your vehicle below.
                                  </Alert>
                                )}
                                <div className="verification-confirmation-controls">
                                  <Form.Label>Code</Form.Label>
                                  <Form.Control
                                    name="confirmationCode"
                                    id="confirmationCode"
                                    maxLength="6"
                                    onKeyPress={(target) => {
                                      if (!!formProps.errors.confirmationCode || vehicleSearchInProgress) {
                                        return;
                                      }

                                      if (target.charCode == 13) {
                                        _handleConfirmVerification(formProps.values.confirmationCode);
                                        return;
                                      }
                                    }}
                                    onChange={(e) => {
                                      formProps.setFieldValue('confirmationCode', e.target.value);
                                    }}
                                    onBlur={formProps.handleBlur}
                                    value={formProps.values.confirmationCode}
                                    isInvalid={formProps.touched.confirmationCode && !!formProps.errors.confirmationCode}
                                    isValid={formProps.touched.confirmationCode && !formProps.errors.confirmationCode}
                                    data-lp-ignore="true"
                                  />
                                  <Button variant="dark" disabled={!!formProps.errors.confirmationCode || vehicleSearchInProgress} onClick={!vehicleSearchInProgress ? () => { _handleConfirmVerification(formProps.values.confirmationCode); } : null}>
                                    Confirm
                                  </Button>
                                </div>
                                {vehicleSearchInProgress && <Spinner animation="grow" size="sm" />}
                                <Form.Control.Feedback type="invalid">
                                  <p>{formProps.errors.confirmationCode}</p>
                                </Form.Control.Feedback>
                                {!formProps.errors.confirmationCode && confirmationCodeErrorMessage && (
                                  <Form.Text>
                                    <p className="text-danger">
                                      {confirmationCodeErrorMessage !== null && confirmationCodeErrorMessage}
                                    </p>
                                  </Form.Text>
                                )}
                              </div>
                            )}
                            {props.findVehicle && props.findVehicle.vehicles && props.findVehicle.vehicles.length > 0 && (
                              <div className="find-vehicle-options">
                                <h6>Which vehicle needs service?</h6>
                                <ul className="find-vehicle-option-buttons">
                                  {props.findVehicle.vehicles.map((vehicle, index) =>
                                    (
                                      <li key={index}>
                                        <Accordion.Toggle
                                          as={Button}
                                          onClick={() => {
                                            _handleChooseFoundVehicle(vehicle);
                                            if ((vinRequired && !vehicle.vehicle_vin) || mileageRequired) {
                                              // If VIN is required but not obtained from the chosen vehicle
                                              // or if mileage is required, then we'll proceed to Your Vehicle
                                              // Force validation so the user can tell what they need to do next.
                                              formProps.setFieldTouched('vin', true);
                                              formProps.setFieldTouched('mileage', true);
                                              formProps.validateForm();
                                            }
                                          }}
                                          variant="outline-dark"
                                          eventKey={((vinRequired && !vehicle.vehicle_vin) || mileageRequired ? "Vehicle" : "Contact")}
                                          disabled={!!formProps.errors.findPhoneNumber || vehicleSearchInProgress}
                                        >
                                          {vehicle.vehicle_year} {vehicle.vehicle_make} {vehicle.vehicle_model}
                                        </Accordion.Toggle>
                                      </li>
                                    )
                                  )}
                                  <li key="dont-see-my-vehicle">
                                    <Accordion.Toggle
                                      as={Button}
                                      onClick={() => {
                                        _removeChosenVehicleInfo();
                                        _handleVehicleNotListed();
                                      }}
                                      variant="outline-dark"
                                      eventKey="Vehicle"
                                      disabled={!!formProps.errors.findPhoneNumber || vehicleSearchInProgress}
                                    >
                                      I don't see my vehicle
                                    </Accordion.Toggle>
                                  </li>
                                </ul>
                              </div>
                            )}
                          </div>
                        </Card.Body>
                      </Accordion.Collapse>
                    </Card>
                  }

                  {
                    (
                      // Find vehicle is disabled, just need to make sure appointment time was selected
                      (!findVehicleEnabled && appointmentTime) ||
                      // Find vehicle is enabled, make sure appointment time was selected and vehicle search is complete
                      (findVehicleEnabled && appointmentTime && vehicleSearchComplete) ||
                      // Find vehicle is enabled but no vehicles were found after mobile phone entered
                      (findVehicleEnabled && appointmentTime && vehicleSearchNoVehicleFound) ||
                      // A found vehicle was chosen
                      (findVehicleEnabled && appointmentTime && formProps.values.year)
                    ) &&
                    <Card>
                      <Card.Header>
                        <Row xs={1} sm={1} md={2} lg={2}>
                          <Col>
                            <Accordion.Toggle as={Button} eventKey="Vehicle" variant="dark">
                              Your Vehicle
                            </Accordion.Toggle>
                          </Col>
                            {
                              formProps.values.year && _makeName && _modelName ?
                                <Col>
                                  {` ${formProps.values.year} ${_makeName} ${_modelName}`}
                                  {formProps.values.vin && !formProps.errors.vin && (<><br />VIN: {formProps.values.vin}</>)}
                                  {formProps.values.mileage && !formProps.errors.mileage && (<><br />Mileage: {formProps.values.mileage}</>)}
                                </Col> :
                                <Col>
                                  Please tell us about your vehicle.
                                </Col>
                            }
                        </Row>
                      </Card.Header>
                      <Accordion.Collapse eventKey="Vehicle">
                        <Card.Body>
                          {vinDisplayed && (
                            <Row xs={1} sm={1} md={1} lg={1} className={'vin-field'}>
                              <Col>
                                <Form.Row>
                                  <Form.Group as={Col} controlId="vin">
                                    <Form.Label>Vehicle Identification Number (VIN)</Form.Label>
                                    <Form.Control
                                      name="vin"
                                      maxLength="20"
                                      onChange={ (e) => { _handleChangeVin(e.target.value); }}
                                      onBlur={formProps.handleBlur}
                                      value={formProps.values.vin}
                                      isInvalid={formProps.touched.vin && !!formProps.errors.vin}
                                      isValid={formProps.touched.vin && !formProps.errors.vin}
                                    />

                                    <Form.Control.Feedback type="invalid">
                                      <p>{formProps.errors.vin}</p>
                                    </Form.Control.Feedback>

                                    {props.decodeVin.error && (
                                      <Form.Text className="text-warning">
                                        {props.decodeVin.error}
                                      </Form.Text>
                                    )}

                                    {!(formProps.touched.vin && !!formProps.errors.vin) && (
                                      <Form.Text className="text-muted">
                                        <p>
                                          {props.concerns[formProps.values.concern].attributes.ask_for_vin == 'optional' && (
                                            <>
                                              If you don't have your VIN press 'Select Year' below to choose your vehicle.<br />
                                              Depending on your vehicle or service needs, we may need to contact you for the VIN.
                                            </>
                                          )}
                                          {props.concerns[formProps.values.concern].attributes.ask_for_vin == 'required' && (
                                            <>
                                              Your vehicle's VIN is required to schedule an appointment.
                                            </>
                                          )}
                                        </p>
                                      </Form.Text>
                                    )}

                                    <Form.Text className="text-muted">
                                      <p className="where-vin">
                                        <OverlayTrigger
                                          trigger="click"
                                          key="where-vin"
                                          placement="bottom"
                                          overlay={
                                            <Popover id="where-vin-popover">
                                              <Popover.Title as="h3">Where can I find my VIN?</Popover.Title>
                                              <Popover.Content>
                                                <p>
                                                  The 17-digit VIN (Vehicle Identification Number) is a unique code assigned
                                                  to every vehicle that can be used to identify its make, model, year, and other important details.
                                                  Here are some common places where you can find the VIN for your car:
                                                </p>
                                                <p>
                                                  <strong>Dashboard</strong>: The most common location for the VIN is on the
                                                  driver's side dashboard, visible through the windshield. The VIN
                                                  is usually printed on a metal plate and can be seen from outside the car.
                                                </p>
                                                <p>
                                                  <strong>Driver's side door</strong>: Another common location for the VIN is on
                                                  the driver's side door jamb, near the latch mechanism. The VIN may
                                                  be printed on a sticker or a metal plate.
                                                </p>
                                                <p>
                                                  <strong>Registration and insurance documents</strong>: The VIN is often
                                                  listed on the vehicle registration and insurance documents.
                                                </p>
                                                <p>
                                                  <strong>On Recall Letter</strong>: The VIN is typically included on
                                                  recall letters received from your vehicle's manufacturer.
                                                </p>
                                                <p>
                                                  <strong>Vehicle title</strong>: The VIN is also listed on the vehicle title.
                                                </p>
                                              </Popover.Content>
                                            </Popover>
                                          }
                                        >
                                          <a href="#" onClick={(e) => { e.preventDefault(); return false; }}>Where can I find my VIN?</a>
                                        </OverlayTrigger>
                                      </p>
                                    </Form.Text>
                                  </Form.Group>
                                </Form.Row>
                              </Col>
                            </Row>
                          )}

                          {mileageDisplayed && (
                            <Row xs={1} sm={1} md={1} lg={1} className={'mileage-field'}>
                              <Col>
                                <Form.Row>
                                  <Form.Group as={Col} controlId="mileage">
                                    <Form.Label>Vehicle Mileage (Odometer)</Form.Label>
                                    <Form.Control
                                      name="mileage"
                                      maxLength="10"
                                      onChange={ (e) => { _handleChangeMileage(e.target.value); }}
                                      onBlur={formProps.handleBlur}
                                      value={formProps.values.mileage}
                                      isInvalid={formProps.touched.mileage && !!formProps.errors.mileage}
                                      isValid={formProps.touched.mileage && !formProps.errors.mileage}
                                    />

                                    <Form.Control.Feedback type="invalid">
                                      <p>{formProps.errors.mileage}</p>
                                    </Form.Control.Feedback>

                                    {!(formProps.touched.mileage && !!formProps.errors.mileage) && (
                                      <Form.Text className="text-muted">
                                        <p>
                                          {props.concerns[formProps.values.concern].attributes.ask_for_mileage == 'optional' && (
                                            <>
                                              Mileage is optional, however it does help when diagnosing your vehicle concerns.
                                            </>
                                          )}
                                          {props.concerns[formProps.values.concern].attributes.ask_for_mileage == 'required' && (
                                            <>
                                              Your vehicle's mileage is required to schedule an appointment.
                                            </>
                                          )}
                                        </p>
                                      </Form.Text>
                                    )}
                                  </Form.Group>
                                </Form.Row>
                              </Col>
                            </Row>
                          )}

                          <ChildAccordion defaultActiveKey={(formProps.values.year == '') ? "Year" : null}>
                            <Card>
                              <Card.Header>
                                <Row xs={1} sm={1} md={2} lg={2}>
                                  <Col>
                                    <Accordion.Toggle as= {Button} variant="dark" eventKey="Year">
                                      Select Year
                                    </Accordion.Toggle>
                                  </Col>
                                  <Col>
                                    {formProps.values.year}
                                  </Col>
                                </Row>
                              </Card.Header>

                              <Accordion.Collapse eventKey="Year">
                                <Card.Body>
                                  <Row xs={2} md={4} lg={6}>
                                    {
                                      props.years.map(
                                        (year, indx) => (
                                          <Col key={year}>
                                            <Accordion.Toggle
                                              as={Button}
                                              variant={ formProps.values.year === year ? 'dark' : 'outline-dark' }
                                              onClick={ () => {
                                                _handleChangeYear(year);
                                                _removeChosenVehicleInfo();
                                              } }
                                              key={year}
                                              eventKey="Make"
                                              className="optionButton"
                                            >
                                              {year}
                                            </Accordion.Toggle>
                                          </Col>
                                        )
                                      )
                                    }
                                  </Row>
                                </Card.Body>
                              </Accordion.Collapse>
                            </Card>
                            {
                              formProps.values.year && Object.keys(props.makes).length > 0 &&
                              <Card>
                                <Card.Header>
                                  <Row xs={1} sm={1} md={2} lg={2}>
                                    <Col>
                                      <Accordion.Toggle as= {Button} variant="dark" eventKey="Make">
                                        Select Make
                                      </Accordion.Toggle>
                                    </Col>
                                      {
                                        _makeName &&
                                        <Col>
                                          {_makeName}
                                        </Col>
                                      }
                                  </Row>
                                </Card.Header>
                                <Accordion.Collapse eventKey="Make">
                                  <Card.Body>
                                    <Row xs={1} md={2} lg={3}>
                                      {
                                        Object.keys(props.makes).map(
                                          (make, indx) => (
                                            <Col key={props.makes[make].id} >
                                              <Accordion.Toggle
                                                as={Button}
                                                variant={ formProps.values.make === props.makes[make].id ? 'dark' : 'outline-dark' }
                                                onClick={ () => {
                                                  _handleChangeMake(props.makes[make].id);
                                                  _removeChosenVehicleInfo();
                                                } }
                                                eventKey="Model"
                                                className="optionButton"
                                              >
                                                {props.makes[make].attributes.make_display}
                                              </Accordion.Toggle>
                                            </Col>
                                          )
                                        )
                                      }
                                    </Row>
                                  </Card.Body>
                                </Accordion.Collapse>
                              </Card>
                            }
                            {
                              formProps.values.year && Object.keys(props.makes).length > 0 &&
                              <Card>
                                <Card.Header>
                                  <Row xs={1} sm={1} md={2} lg={2}>
                                    <Col>
                                      <Accordion.Toggle as={Button} variant="dark" eventKey="Model">
                                        Select Model
                                      </Accordion.Toggle>
                                    </Col>
                                    {
                                      _modelName &&
                                      <Col>
                                        {_modelName}
                                      </Col>
                                    }
                                  </Row>
                                </Card.Header>
                                <Accordion.Collapse eventKey="Model">
                                  <Card.Body>
                                    <Row xs={1} md={2} lg={3}>
                                      {
                                        Object.keys(props.models).map(
                                          (model, indx) => {
                                            // The Model Accordion behavior differs depending on whether
                                            // the VIN and/or the mileage fields are required.
                                            //
                                            // If there is a missing required field, the action will be to just
                                            // close the model  selection and wait for the required fields.
                                            //
                                            // If all the fields are given (or fields are blank but optional)
                                            // the form will proceed to contact accordion.
                                            let ModelAccordionEventKey = (
                                              (vinRequired && !!formProps.errors.vin) ||
                                              (mileageRequired && !!formProps.errors.mileage)
                                            ) ? 'Model' : 'Contact';

                                            let ModelAccordionComponent = (ModelAccordionEventKey == 'Model')
                                              ? Accordion.Toggle : ChildAccordion.Toggle;

                                            return <Col key={props.models[model].id} >
                                              <ModelAccordionComponent
                                                as={Button}
                                                eventKey={ModelAccordionEventKey}
                                                variant={ formProps.values.model === props.models[model].id ? 'dark' : 'outline-dark' }
                                                onClick={ () => {
                                                  _handleChangeModel(props.models[model].id);
                                                  _removeChosenVehicleInfo();

                                                  if (vinRequired) {
                                                    // If VIN is required and they've proceeded through
                                                    // Year, Make, Model selection and still do not have
                                                    // a VIN number, the Next button will be disabled.
                                                    // Force the VIN field "touched" so the required VIN
                                                    // error message will appear for additional direction.
                                                    formProps.setFieldTouched('vin', true);
                                                    formProps.validateForm();
                                                  }
                                                } }
                                                className="optionButton"
                                              >
                                                {props.models[model].attributes.model_name}
                                              </ModelAccordionComponent>
                                            </Col>
                                          }
                                        )
                                      }
                                    </Row>
                                  </Card.Body>
                                </Accordion.Collapse>
                              </Card>
                            }
                          </ChildAccordion>

                          {(vinRequired || mileageRequired) && (
                            <Row>
                              <Col>
                                <br/>
                                <Accordion.Toggle
                                  as={Button}
                                  eventKey="Contact"
                                  variant="dark"
                                  disabled={!vehicleInformationEntered}
                                >
                                  Next
                                </Accordion.Toggle>
                              </Col>
                            </Row>
                          )}
                        </Card.Body>
                      </Accordion.Collapse>
                    </Card>
                  }

                  {
                    appointmentTime && vehicleInformationEntered &&
                    <Card>
                      <Card.Header>
                        <Row xs={1} sm={1} md={2} lg={2}>
                          <Col>
                            <Accordion.Toggle as={Button} eventKey="Contact" variant="dark">
                              Contact Information
                            </Accordion.Toggle>
                          </Col>
                            {
                              formProps.values.name ?
                                <Col>
                                  {formProps.values.name}<br/>
                                  {
                                    formProps.values.phoneNumber &&
                                      `${formProps.values.phoneNumber}`
                                  }<br/>
                                  {
                                    formProps.values.email &&
                                      `${formProps.values.email}`
                                  }
                                </Col> :
                                <Col>
                                  Please provide your contact information.
                                </Col>
                            }
                        </Row>
                      </Card.Header>
                      <Accordion.Collapse eventKey="Contact">
                        <Card.Body>
                          <Form.Row>
                            <Form.Group as={Col} controlId="name">
                              <Form.Label>Contact Information</Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Name"
                                name="name"
                                onChange={formProps.handleChange}
                                onBlur={formProps.handleBlur}
                                value={formProps.values.name}
                                isInvalid={formProps.touched.name && !!formProps.errors.name}
                                isValid={formProps.touched.name && !formProps.errors.name}
                              />
                              <Form.Control.Feedback type="invalid">
                                {formProps.errors.name}
                              </Form.Control.Feedback>
                              <Form.Text className="text-muted">
                                Your name
                              </Form.Text>
                            </Form.Group>
                          </Form.Row>
                          <Form.Row>
                            <Form.Group as={Col} controlId="phoneNumber">
                              <Form.Label>Mobile Phone Number</Form.Label>
                              <MaskedInput
                                name="phoneNumber"
                                mask="(000) 000-0000"
                                lazy={false}
                                onAccept={(value) => formProps.setFieldValue('phoneNumber', value)}
                                onBlur={formProps.handleBlur}
                                value={formProps.values.phoneNumber}
                                isInvalid={formProps.values.phoneNumber.length && !!formProps.errors.phoneNumber}
                                isValid={formProps.values.phoneNumber.length && !formProps.errors.phoneNumber}
                                inputMode="numeric"
                                data-lp-ignore="true"
                              />
                              <Form.Control.Feedback type="invalid">
                                {formProps.errors.phoneNumber}
                              </Form.Control.Feedback>
                              <Form.Text className="text-muted">
                                {requireOptInConsent ? (
                                  <>
                                    <Form.Check type="checkbox" id="optInConsent" onChange={(e) => _handleOptInConsentChange(e.target.checked && e.target.value) } />
                                    <label id="opt-in-consent-label" htmlFor="optInConsent">
                                      <p>
                                        Your mobile phone number is required to make an appointment.
                                        By submitting my cell phone number, I agree to receive text messages from {props.organization.name} in the form of appointment reminders which may be sent using
                                        automated software, unless I opt-out from such communications. I understand I can opt-out at any time by replying STOP. I agree to pay my mobile service provider's text
                                        messaging rates, if applicable. For full details read the <a href="https://kabiauto.com/privacy" target="_blank">text messaging program privacy policy &amp; terms</a>.&nbsp;
                                      </p>
                                      {formProps.errors.optInConsent && (
                                        <div>
                                          <span className="optInConsentFeedback">{formProps.errors.optInConsent}</span>
                                        </div>
                                      )}
                                    </label>
                                  </>
                                ) : (
                                  <>Your mobile phone number is required to make an appointment. We may contact you about your appointment.</>
                                )}
                              </Form.Text>
                            </Form.Group>
                          </Form.Row>
                          <Form.Row>
                            <Form.Group as={Col} controlId="email">
                              <Form.Label>Email Address</Form.Label>
                              <Form.Control
                                type="email"
                                placeholder="Email"
                                name="email"
                                onChange={formProps.handleChange}
                                onBlur={formProps.handleBlur}
                                value={formProps.values.email}
                                isInvalid={formProps.touched.email && !!formProps.errors.email}
                                isValid={formProps.touched.email && !formProps.errors.email}
                              />
                              <Form.Control.Feedback type="invalid">
                                {formProps.errors.email}
                              </Form.Control.Feedback>
                              <Form.Text className="text-muted">
                                Your email address is required. It may be used to communicate appointment & job information.
                              </Form.Text>
                            </Form.Group>
                          </Form.Row>
                          <Row>
                            <Col>
                              <Accordion.Toggle
                                as={Button}
                                eventKey="Contact"
                                variant="dark"
                                disabled={formProps.errors.name || formProps.errors.email || formProps.errors.phoneNumber || formProps.errors.optInConsent}
                                onClick={_handleReview}
                              >
                                Review
                              </Accordion.Toggle>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Accordion.Collapse>
                    </Card>
                  }

                  {
                    showSubmit && appointmentTime &&
                      <Card>
                        <Card.Header>
                          <Form.Row>
                            <Form.Group>
                              <Form.Text className="text-muted">
                                By clicking "Submit Appointment" and providing my Mobile number and/or email address, I agree to receive information related to the service needs of my vehicle. I understand there may be charges associated with text messaging.
                              </Form.Text>
                            </Form.Group>
                          </Form.Row>
                          <Form.Group as={Row}>
                            <Col>
                              <Button type="submit" variant="dark" disabled={!formProps.isValid || formProps.isSubmitting}>
                                Submit Appointment
                              </Button>
                            </Col>
                            {
                              formProps.isValid ?
                                <Col><div className="g-recaptcha" data-sitekey="6Lc9Pv4UAAAAAL8u9QO3c178r3BE4PTXSEFXXjvL"></div></Col> :
                                <Col className="invalid-feedback override-display">
                                  Please review your form for errors and completeness.
                                  <div>
                                    {Object.keys(formProps.errors).map((key, index) => (
                                      <p key={index}>{startCase(key)}: {formProps.errors[key]}</p>
                                    ))}
                                  </div>
                                </Col>
                            }
                          </Form.Group>
                        </Card.Header>
                      </Card>
                  }
                </Accordion>
              }
              <Row><Col>Patent &#9413; &amp; Copyright &copy; {new Date().getFullYear()} KABI, LLC - All Rights Reserved.</Col></Row>
            </Form>
          );
        }
      }
    </Formik>
  );
}

const VehicleScreen = props =>
  <Container fluid>
    <Row>
      <Col>
        <AppointmentForm {...props} />
      </Col>
    </Row>
  </Container>

const mapState = state => ({
  years: Years,
  makes: state.makes,
  models: state.models,
  concerns: state.concerns,
  concernIdsSorted: store.select.concerns.sortedIds(state),
  advisors: state.advisors,
  appointments: advisorId => store.select.appointments.filteredByAdvisor(state, { advisorId }),
  unfilteredAppointments: state.appointments,
  appointment: state.appointment,
  organization: state.organization,
  decodeVin: state.decodeVin,
  findVehicle: state.findVehicle,
  updateVerification: state.updateVerification,
  potentiallyMatchingSkills: state.potentiallyMatchingSkills,
  skillClassifications: state.skillClassifications,
  state,
  formattedAppointment: store.select.appointment.formatted(state),
  appointmentSearchCompletedAt: state.appointment?.appointmentSearchCompletedAt,
  minDate: state.appointment.minDate,
})

const mapDispatch = (dispatch) => ({
  fetchMakes: dispatch.makes.fetchAsync,
  clearModels: dispatch.models.clear,
  fetchModels: dispatch.models.fetchAsync,
  fetchConcerns: dispatch.concerns.fetchAsync,
  fetchAdvisors: dispatch.advisors.fetchAsync,
  fetchAppointments: dispatch.appointments.fetchAsync,
  fetchOrganization: dispatch.organization.fetchAsync,
  fetchDecodeVin: dispatch.decodeVin.fetchAsync,
  fetchFindVehicle: dispatch.findVehicle.fetchAsync,
  fetchCreateVerification: dispatch.createVerification.fetchAsync,
  fetchUpdateVerification: dispatch.updateVerification.fetchAsync,
  fetchSkillClassifications: dispatch.skillClassifications.fetchAsync,
  updateAppointment: async values => {
    await dispatch.appointment.replace(values);
    await dispatch.appointment.submitAsync();
  },
});

export default connect(
  mapState,
  mapDispatch
)(VehicleScreen);
